// The RJSF theme: @rjsf/core templates and widgets rendered with the BOKU primitives
// from components/Shared.jsx, so a JSON-Schema form looks like every other form here.
//
// Accessibility: every control gets a visible <label for>, aria-required,
// aria-invalid and aria-describedby pointing at its description and its errors;
// groups are <fieldset>/<legend>; the error summary is role="alert" and links to the
// fields. Array buttons carry text ("Remove creator 2"), not only icons.
import { useCallback } from "react";
import {
  descriptionId, enumOptionsIndexForValue, enumOptionsValueForIndex, errorId,
  getInputProps, getTemplate, getUiOptions, helpId, titleId,
} from "@rjsf/utils";
import { Btn, G_DARK, Input, Select, Textarea } from "../components/Shared";

const labelStyle = { fontSize: 12, fontWeight: 600, color: "#374151", letterSpacing: "0.05em", textTransform: "uppercase" };
const hintStyle = { fontSize: 11, color: "#6b7280", margin: 0 };
const errorStyle = { fontSize: 12, color: "#b91c1c", margin: 0, padding: 0, listStyle: "none" };
const groupStyle = { border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 18px 18px", margin: 0, minWidth: 0 };
const legendStyle = { fontSize: 14, fontWeight: 700, color: "#1a1a1a", padding: "0 6px" };
const srOnly = { position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" };

function invalidProps(rawErrors) {
  return rawErrors && rawErrors.length ? { "aria-invalid": true } : {};
}

// aria-describedby naming only the parts that are rendered. RJSF's ariaDescribedByIds() always
// lists the error, description and help ids, and a reference to a missing id is invalid ARIA
// (axe-core aria-valid-attr-value, 2026-10-04).
function describedBy(id, schema, uiSchema, rawErrors) {
  const ui = getUiOptions(uiSchema);
  return [
    rawErrors && rawErrors.filter(Boolean).length ? errorId(id) : null,
    ui.description || (schema && schema.description) ? descriptionId(id) : null,
    ui.help ? helpId(id) : null,
  ].filter(Boolean).join(" ") || undefined;
}

// ── Templates ────────────────────────────────────────────────────────────────

function FieldTemplate(props) {
  const { id, label, children, errors, help, description, hidden, required, displayLabel,
          uiSchema, schema, rawDescription } = props;
  if (hidden) return <div style={{ display: "none" }}>{children}</div>;
  const uiOptions = getUiOptions(uiSchema);
  // A custom `ui:field` gets no label from RJSF; it is a single control, so it is
  // labelled here like any input.
  const customField = !!(uiSchema && uiSchema["ui:field"]);
  const isBoolean = schema.type === "boolean" && uiOptions.widget !== "select";
  const showLabel = (displayLabel || customField) && !isBoolean;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
      {showLabel && label && (
        <label htmlFor={id} style={labelStyle}>
          {label}
          {required && <span aria-hidden="true" style={{ color: "#b91c1c", marginLeft: 4 }}>*</span>}
        </label>
      )}
      {children}
      {(showLabel || isBoolean) && rawDescription ? description : null}
      {errors}
      {help}
    </div>
  );
}

function DescriptionFieldTemplate({ id, description }) {
  if (!description) return null;
  return <p id={id} style={hintStyle}>{description}</p>;
}

function TitleFieldTemplate({ id, title, required }) {
  return (
    <legend id={id} style={legendStyle}>
      {title}
      {required && <span aria-hidden="true" style={{ color: "#b91c1c", marginLeft: 4 }}>*</span>}
    </legend>
  );
}

function FieldErrorTemplate({ errors = [], fieldPathId }) {
  const list = errors.filter(Boolean);
  if (!list.length) return null;
  return (
    <ul id={errorId(fieldPathId)} style={errorStyle}>
      {list.map((e, i) => <li key={i}>{e}</li>)}
    </ul>
  );
}

function FieldHelpTemplate({ help, fieldPathId }) {
  if (!help) return null;
  return <p id={helpId(fieldPathId)} style={hintStyle}>{help}</p>;
}

function ErrorListTemplate({ errors }) {
  if (!errors || !errors.length) return null;
  return (
    <div role="alert" style={{ padding: "12px 16px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13, marginBottom: 18 }}>
      <p style={{ margin: "0 0 6px", fontWeight: 700 }}>
        Please correct {errors.length} problem{errors.length === 1 ? "" : "s"} before saving:
      </p>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {errors.map((e, i) => {
          const path = (e.property || "").replace(/^\./, "").split(".").filter(Boolean);
          const target = path.length ? `root_${path.join("_")}` : null;
          return (
            <li key={i}>
              {target
                ? <a href={`#${target}`} style={{ color: "#b91c1c" }}
                     onClick={ev => { const el = document.getElementById(target); if (el) { ev.preventDefault(); el.focus(); } }}>
                    {e.stack}
                  </a>
                : e.stack}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ObjectFieldTemplate(props) {
  const { title, description, properties, fieldPathId, required, uiSchema, registry, schema } = props;
  const uiOptions = getUiOptions(uiSchema);
  const visible = properties.filter(p => !p.hidden);
  const hidden = properties.filter(p => p.hidden);
  if (fieldPathId.$id === "root") {
    return (
      <div id={fieldPathId.$id} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {visible.map(p => <div key={p.name}>{p.content}</div>)}
        {hidden.map(p => p.content)}
      </div>
    );
  }
  const TitleTpl = getTemplate("TitleFieldTemplate", registry, uiOptions);
  const DescriptionTpl = getTemplate("DescriptionFieldTemplate", registry, uiOptions);
  const shownTitle = uiOptions.title || title;
  const content = (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
      {visible.map(p => <div key={p.name} style={{ minWidth: 0 }}>{p.content}</div>)}
      {hidden.map(p => p.content)}
    </div>
  );
  // Objects inside an array item: the item container is already the named fieldset.
  const path = fieldPathId.path || [];
  const isArrayItem = typeof path[path.length - 1] === "number";
  if (!shownTitle || isArrayItem) return <div id={fieldPathId.$id}>{content}</div>;
  return (
    <fieldset id={fieldPathId.$id} style={groupStyle} aria-describedby={description ? descriptionId(fieldPathId) : undefined}>
      <TitleTpl id={titleId(fieldPathId)} title={shownTitle} required={required} schema={schema}
                           uiSchema={uiSchema} registry={registry} />
      {description && (
        <div style={{ marginBottom: 12 }}>
          <DescriptionTpl id={descriptionId(fieldPathId)} description={uiOptions.description || description}
                                     schema={schema} uiSchema={uiSchema} registry={registry} />
        </div>
      )}
      {content}
    </fieldset>
  );
}

function ArrayFieldTemplate(props) {
  const { canAdd, disabled, readonly, fieldPathId, uiSchema, items, onAddClick, required, schema,
          title, registry, rawErrors } = props;
  const uiOptions = getUiOptions(uiSchema);
  const shownTitle = uiOptions.title || title;
  const description = uiOptions.description || schema.description;
  const addLabel = uiOptions.addLabel || "Add";
  const errs = (rawErrors || []).filter(Boolean);
  return (
    <fieldset id={fieldPathId.$id} style={groupStyle}
              aria-describedby={[description && descriptionId(fieldPathId), errs.length && errorId(fieldPathId)].filter(Boolean).join(" ") || undefined}>
      <TitleFieldTemplate id={titleId(fieldPathId)} title={shownTitle} required={required} />
      {description && <p id={descriptionId(fieldPathId)} style={{ ...hintStyle, marginBottom: 12 }}>{description}</p>}
      {items.length === 0 && (
        <p style={{ ...hintStyle, fontSize: 13, marginBottom: 8 }}>
          {readonly || disabled ? "None." : "None yet."}
        </p>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{items}</div>
      {errs.length > 0 && (
        <ul id={errorId(fieldPathId)} style={{ ...errorStyle, marginTop: 8 }}>{errs.map((e, i) => <li key={i}>{e}</li>)}</ul>
      )}
      {canAdd && !readonly && !disabled && (
        <div style={{ marginTop: 12 }}>
          <registry.templates.ButtonTemplates.AddButton onClick={onAddClick} disabled={disabled || readonly}
                                                        registry={registry} uiSchema={uiSchema}
                                                        label={addLabel} />
        </div>
      )}
    </fieldset>
  );
}

function ArrayFieldItemTemplate(props) {
  const { children, buttonsProps, hasToolbar, index, parentUiSchema, readonly, disabled } = props;
  const opts = getUiOptions(parentUiSchema);
  const itemLabel = opts.itemLabel || "Item";
  const { hasMoveUp, hasMoveDown, hasRemove, onMoveUpItem, onMoveDownItem, onRemoveItem } = buttonsProps;
  const locked = readonly || disabled;
  const n = index + 1;
  const headerId = `${buttonsProps.fieldPathId.$id}__item`;
  return (
    <div role="group" aria-labelledby={headerId}
         style={{ background: "#f9fafb", border: "1px solid #eef0f2", borderRadius: 10, padding: "10px 14px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
        <span id={headerId} style={{ fontSize: 12, fontWeight: 700, color: G_DARK }}>{itemLabel} {n}</span>
        {hasToolbar && !locked && (
          <span style={{ display: "inline-flex", gap: 6 }}>
            {(hasMoveUp || hasMoveDown) && (
              <>
                <Btn variant="secondary" disabled={!hasMoveUp} onClick={onMoveUpItem} style={smallBtn}>
                  <span aria-hidden="true">↑</span><span style={srOnly}>Move {itemLabel.toLowerCase()} {n} up</span>
                </Btn>
                <Btn variant="secondary" disabled={!hasMoveDown} onClick={onMoveDownItem} style={smallBtn}>
                  <span aria-hidden="true">↓</span><span style={srOnly}>Move {itemLabel.toLowerCase()} {n} down</span>
                </Btn>
              </>
            )}
            {hasRemove && (
              <Btn variant="secondary" onClick={onRemoveItem} style={smallBtn}>
                Remove<span style={srOnly}> {itemLabel.toLowerCase()} {n}</span>
              </Btn>
            )}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

const smallBtn = { padding: "4px 10px", fontSize: 12 };

function AddButton({ onClick, disabled, label = "Add" }) {
  return (
    <Btn variant="ghost" onClick={onClick} disabled={disabled}>
      <span aria-hidden="true">+</span> {label}
    </Btn>
  );
}

function SubmitButton() {
  return <Btn type="submit">Save</Btn>;
}

function NoButton() {
  return null;
}

function BaseInputTemplate(props) {
  const { id, htmlName, value, readonly, disabled, autofocus, onBlur, onFocus, onChange, onChangeOverride,
          options, schema, uiSchema, type, placeholder, required, rawErrors, label, hideLabel } = props;
  const inputProps = getInputProps(schema, type, options);
  let inputValue;
  if (inputProps.type === "number" || inputProps.type === "integer") inputValue = value || value === 0 ? value : "";
  else inputValue = value == null ? "" : value;
  const handleChange = useCallback(e => onChange(e.target.value === "" ? options.emptyValue : e.target.value),
                                   [onChange, options]);
  return (
    <Input
      id={id}
      name={htmlName || id}
      {...inputProps}
      value={inputValue}
      placeholder={placeholder}
      readOnly={readonly}
      disabled={disabled}
      autoFocus={autofocus}
      required={required}
      aria-required={required || undefined}
      aria-label={hideLabel ? label : undefined}
      aria-describedby={describedBy(id, schema, uiSchema, rawErrors)}
      {...invalidProps(rawErrors)}
      onChange={onChangeOverride || handleChange}
      onBlur={e => onBlur(id, e.target.value)}
      onFocus={e => onFocus(id, e.target.value)}
      style={readonly ? { background: "#f9fafb" } : undefined}
    />
  );
}

// ── Widgets ──────────────────────────────────────────────────────────────────

function TextareaWidget({ id, htmlName, value, readonly, disabled, autofocus, onChange, onBlur, onFocus,
                          options, placeholder, required, rawErrors, schema, uiSchema }) {
  return (
    <Textarea
      id={id}
      name={htmlName || id}
      value={value == null ? "" : value}
      rows={options.rows || 4}
      placeholder={placeholder}
      readOnly={readonly}
      disabled={disabled}
      autoFocus={autofocus}
      required={required}
      aria-required={required || undefined}
      aria-describedby={describedBy(id, schema, uiSchema, rawErrors)}
      {...invalidProps(rawErrors)}
      onChange={e => onChange(e.target.value === "" ? options.emptyValue : e.target.value)}
      onBlur={e => onBlur(id, e.target.value)}
      onFocus={e => onFocus(id, e.target.value)}
    />
  );
}

function SelectWidget({ id, htmlName, value, readonly, disabled, onChange, onBlur, onFocus, options,
                        required, rawErrors, multiple, schema, uiSchema }) {
  const { enumOptions = [], enumDisabled, emptyValue } = options;
  if (multiple) throw new Error("Multiple select is not supported by this theme.");
  const selected = enumOptionsIndexForValue(value, enumOptions, false);
  const toValue = e => enumOptionsValueForIndex(e.target.value, enumOptions, emptyValue);
  return (
    <Select
      id={id}
      name={htmlName || id}
      value={selected === undefined || selected === null ? "" : String(selected)}
      disabled={disabled || readonly}
      required={required}
      aria-required={required || undefined}
      aria-describedby={describedBy(id, schema, uiSchema, rawErrors)}
      {...invalidProps(rawErrors)}
      onChange={e => onChange(toValue(e))}
      onBlur={e => onBlur(id, toValue(e))}
      onFocus={e => onFocus(id, toValue(e))}
      options={enumOptions.map((o, i) => ({
        value: String(i), label: o.label,
        disabled: enumDisabled && enumDisabled.includes(o.value),
      }))}
    />
  );
}

function CheckboxWidget({ id, value, readonly, disabled, onChange, label, required, rawErrors, schema, uiSchema }) {
  return (
    <label htmlFor={id} style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, color: "#1a1a1a" }}>
      <input
        id={id}
        type="checkbox"
        checked={!!value}
        disabled={disabled || readonly}
        required={required}
        aria-describedby={describedBy(id, schema, uiSchema, rawErrors)}
        {...invalidProps(rawErrors)}
        onChange={e => onChange(e.target.checked)}
        style={{ width: 16, height: 16, accentColor: "var(--dp-accent-dark)" }}
      />
      {label || schema.title}
    </label>
  );
}

export const widgets = {
  TextareaWidget,
  SelectWidget,
  CheckboxWidget,
};

// No custom fields; exported so a form can pass `fields={fields}` like the rest of the theme.
export const fields = {};

export const templates = {
  FieldTemplate,
  ObjectFieldTemplate,
  ArrayFieldTemplate,
  ArrayFieldItemTemplate,
  BaseInputTemplate,
  DescriptionFieldTemplate,
  TitleFieldTemplate,
  FieldErrorTemplate,
  FieldHelpTemplate,
  ErrorListTemplate,
  ButtonTemplates: {
    AddButton, SubmitButton, RemoveButton: NoButton, MoveUpButton: NoButton, MoveDownButton: NoButton,
    CopyButton: NoButton, ClearButton: NoButton,
  },
};
